import { useState } from 'react'
import type { ConexaoDto } from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useAbrirChamado } from '../../api/hooks-chat'
import { Aviso, Botao, Campo, Modal } from '../../componentes/ui'

/**
 * Começar conversa com um número qualquer.
 *
 * O caminho de antes era sair do bate-papo, ir em Leads, cadastrar o contato,
 * voltar e procurá-lo na lista — para um número que o cliente acabou de ditar
 * ao telefone. Aqui digita-se e pronto.
 *
 * Número que já está na base reaproveita o contato em vez de cadastrar outro:
 * o mesmo cliente virando dois registros é o começo de duas conversas
 * paralelas com ele.
 */
export function NovaConversa({
  aberto,
  conexoes,
  conexaoAtual,
  aoFechar,
  aoAbrir,
}: {
  aberto: boolean
  conexoes: ConexaoDto[]
  conexaoAtual: string | null
  aoFechar: () => void
  /** Recebe o chamado criado, para a tela já abrir nele. */
  aoAbrir: (chamadoId: string) => void
}) {
  const abrir = useAbrirChamado()
  const [telefone, setTelefone] = useState('')
  const [nome, setNome] = useState('')
  const [conexaoId, setConexaoId] = useState(conexaoAtual ?? '')
  const [carregadoDe, setCarregadoDe] = useState(false)

  if (aberto && !carregadoDe) {
    setCarregadoDe(true)
    setTelefone('')
    setNome('')
    setConexaoId(conexaoAtual ?? conexoes[0]?.id ?? '')
  }
  if (!aberto && carregadoDe) setCarregadoDe(false)

  const erro = abrir.error instanceof ErroRequisicao ? abrir.error : null
  const podeEnviar = telefone.replace(/\D/g, '').length >= 10 && Boolean(conexaoId)

  function iniciar() {
    if (!podeEnviar) return
    abrir.mutate(
      { conexaoId, telefone, nome: nome.trim() || undefined },
      {
        onSuccess: (r) => {
          aoAbrir(r.chamadoId)
          aoFechar()
        },
      },
    )
  }

  /*
    Só números com envio liberado.

    Abrir conversa por um número em modo somente leitura criaria um chamado
    que não pode responder — a mensagem ficaria presa antes de sair (ADR 0001).
  */
  const disponiveis = conexoes.filter((c) => !c.somenteLeitura)

  return (
    <Modal
      aberto={aberto}
      titulo="Iniciar conversa"
      aoFechar={aoFechar}
      // Tem número digitado: clique fora sem querer não pode apagar.
      fecharAoClicarFora={false}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            carregando={abrir.isPending}
            disabled={!podeEnviar}
            onClick={iniciar}
          >
            Iniciar conversa
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}

        {disponiveis.length === 0 ? (
          <Aviso tipo="atencao">
            Nenhum número liberado para envio. Em Configuração → Números, tire o modo somente
            leitura de algum deles.
          </Aviso>
        ) : null}

        <Campo
          rotulo="Número do WhatsApp"
          value={telefone}
          onChange={(e) => setTelefone(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') iniciar()
          }}
          placeholder="(31) 99999-6935"
          erro={erro?.campo('telefone')}
          dica="Com DDD. Número novo é cadastrado e conta como prospecção ativa nas estatísticas. Se já estiver na base, abre a conversa do contato que já existe."
          autoFocus
        />

        <Campo
          rotulo="Nome (opcional)"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Como você quer chamar este contato"
          dica="Só é usado quando o número ainda não está cadastrado. Em branco, o próprio número vira o nome."
        />

        {disponiveis.length > 1 ? (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-tinta-800">
              Enviar pelo número
            </span>
            <select
              value={conexaoId}
              onChange={(e) => setConexaoId(e.target.value)}
              className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
            >
              {disponiveis.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.numero}, {c.nome}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <p className="text-xs text-tinta-400">
          A conversa nasce sua, a menos que o contato já esteja na carteira de outra pessoa, e aí
          vai para ela. O cliente só recebe alguma coisa quando você mandar a primeira mensagem.
        </p>
      </div>
    </Modal>
  )
}
